import { format } from 'date-fns';
import type { DateRange } from 'react-day-picker';

export interface WeeklyReportEmailTemplateProps {
  name: string;
  dateRange: DateRange | string;
  last_week_tasks: { value: string; dependency?: string }[];
  Unplanned: { value: string; dependency?: string }[];
  blockers: { value: string; dependency?: string }[];
  next_week_tasks: { value: string; dependency?: string }[];
  further_beyond: { value: string; dependency?: string }[];
  notes?: string;
  to?: string;
  cc?: string;
  bcc?: string;
  greeting?: string;
  signature?: string;
}

const WeeklyReportEmailTemplate = (props: WeeklyReportEmailTemplateProps): string => {
  const {
    name,
    dateRange,
    last_week_tasks,
    Unplanned,
    blockers,
    next_week_tasks,
    further_beyond,
    notes,
    greeting = "Hi All,\nPlease take a look at the report for this week below.",
    signature
  } = props;

  // Get just the name part if name contains an email
  const displayName = name.includes('@') ? name.split('@')[0] : name;

  // Use provided signature or create a default one with just the name
  const defaultSignature = `BR,\n${displayName}`;
  const finalSignature = signature || defaultSignature;

  // Format date range for display
  if (typeof dateRange === 'string') {
    // do nothing
  } else if (dateRange && dateRange.from && dateRange.to) {
    `${format(new Date(dateRange.from), 'MMM d, yyyy')} - ${format(new Date(dateRange.to), 'MMM d, yyyy')}`;
  } else {
    format(new Date(), 'MMM d, yyyy');
  }

  // Check if sections have content
  const hasLastWeekTasks = last_week_tasks && last_week_tasks.length > 0 && last_week_tasks.some(item => item?.value?.trim());
  const hasUnplanned = Unplanned && Unplanned.length > 0 && Unplanned.some(item => item?.value?.trim());
  const hasBlockers = blockers && blockers.length > 0 && blockers.some(item => item?.value?.trim());
  const hasNextWeek = next_week_tasks && next_week_tasks.length > 0 && next_week_tasks.some(item => item?.value?.trim());
  const hasFurtherBeyond = further_beyond && further_beyond.length > 0 && further_beyond.some(item => item?.value?.trim());
  const hasNotes = notes && notes.trim() !== '';

  // Process greeting and signature text to replace newlines with <br> tags
  const formattedGreeting = greeting.replace(/\n/g, '<br>');
  const formattedSignature = finalSignature.replace(/\n/g, '<br>');

  // Generate HTML for the email template
  const html = `
    <div style="font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto 0 0; padding: 20px; color: #333; text-align: left; direction: ltr;">
      <div style="margin-bottom: 20px;">
        <h2 style="color: #2563eb; text-align: left;">Weekly Report</h2>
      </div>
      
      <div style="margin-bottom: 20px;">
        <p style="margin-bottom: 15px;">${formattedGreeting}</p>
      </div>
      
      ${hasLastWeekTasks ? `
      <div style="margin-bottom: 20px;">
        <h3 style="color: #1e40af; border-bottom: 1px solid #e5e7eb; padding-bottom: 8px; text-align: left;">Last Week Tasks</h3>
        <ul style="padding-left: 20px; list-style-position: outside;">
          ${last_week_tasks.filter(item => item?.value?.trim()).map(item => `<li style="margin-bottom: 8px;">${item.value}${item.dependency ? `<span style="font-size: 0.8em; color: #666; margin-left: 5px;"> (Dep: ${item.dependency})</span>` : ''}</li>`).join('')}
        </ul>
      </div>
      ` : ''}
      
      ${hasUnplanned ? `
      <div style="margin-bottom: 20px;">
        <h3 style="color: #1e40af; border-bottom: 1px solid #e5e7eb; padding-bottom: 8px; text-align: left;">Unplanned</h3>
        <ul style="padding-left: 20px; list-style-position: outside;">
          ${Unplanned.filter(item => item?.value?.trim()).map(item => `<li style="margin-bottom: 8px;">${item.value}${item.dependency ? `<span style="font-size: 0.8em; color: #666; margin-left: 5px;"> (Dep: ${item.dependency})</span>` : ''}</li>`).join('')}
        </ul>
      </div>
      ` : ''}
      
      ${hasBlockers ? `
      <div style="margin-bottom: 20px;">
        <h3 style="color: #1e40af; border-bottom: 1px solid #e5e7eb; padding-bottom: 8px; text-align: left;">Blockers</h3>
        <ul style="padding-left: 20px; list-style-position: outside;">
          ${blockers.filter(item => item?.value?.trim()).map(item => `<li style="margin-bottom: 8px;">${item.value}${item.dependency ? `<span style="font-size: 0.8em; color: #666; margin-left: 5px;"> (Dep: ${item.dependency})</span>` : ''}</li>`).join('')}
        </ul>
      </div>
      ` : ''}
      
      ${hasNextWeek ? `
      <div style="margin-bottom: 20px;">
        <h3 style="color: #1e40af; border-bottom: 1px solid #e5e7eb; padding-bottom: 8px; text-align: left;">Next Week Tasks</h3>
        <ul style="padding-left: 20px; list-style-position: outside;">
          ${next_week_tasks.filter(item => item?.value?.trim()).map(item => `<li style="margin-bottom: 8px;">${item.value}${item.dependency ? `<span style="font-size: 0.8em; color: #666; margin-left: 5px;"> (Dep: ${item.dependency})</span>` : ''}</li>`).join('')}
        </ul>
      </div>
      ` : ''}
      
      ${hasFurtherBeyond ? `
      <div style="margin-bottom: 20px;">
        <h3 style="color: #1e40af; border-bottom: 1px solid #e5e7eb; padding-bottom: 8px; text-align: left;">Further Beyond</h3>
        <ul style="padding-left: 20px; list-style-position: outside;">
          ${further_beyond.filter(item => item?.value?.trim()).map(item => `<li style="margin-bottom: 8px;">${item.value}${item.dependency ? `<span style="font-size: 0.8em; color: #666; margin-left: 5px;"> (Dep: ${item.dependency})</span>` : ''}</li>`).join('')}
        </ul>
      </div>
      ` : ''}
      
      ${hasNotes ? `
      <div style="margin-bottom: 20px;">
        <h3 style="color: #1e40af; border-bottom: 1px solid #e5e7eb; padding-bottom: 8px; text-align: left;">Additional Notes</h3>
        <p style="line-height: 1.6;">${notes}</p>
      </div>
      ` : ''}

      <div style="margin-top: 30px; text-align: left;">
        <p>${formattedSignature}</p>
      </div>
      
      <div style="margin-top: 30px; border-top: 1px solid #e5e7eb; padding-top: 10px; text-align: left;">
        <p style="color: #6b7280; font-size: 10px;">Generated by Weekly Report</p>
      </div>
    </div>
  `;

  return html;
};

export default WeeklyReportEmailTemplate;